import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolCard } from '../../types/agent';
import { toolLabel } from '../../services/agent/toolCatalogue';
import { qualify } from '../../services/excel/a1';
import { StepDot } from './StepsList';

/** From this many steps on, a run is a batch and gets the card rather than one row per call.
 *
 *  `StepsList` shows every step that has not settled, which was written for the handful of calls a chat turn
 *  makes. A Change Plan is one batch of forty-eight, and they all arrive `pending` at the same instant: the
 *  pane filled with forty-eight identical "Writing formulas" rows, the one line that said anything —
 *  "4 of 48 · company_asset" — pushed off the bottom. */
export const BATCH_MIN = 5;

interface SheetRun {
  sheet: string;
  cards: ToolCard[];
  done: number;
  failed: number;
  running: boolean;
}

const SETTLED: ToolCard['status'][] = ['done', 'error', 'cancelled'];

/** The batch by sheet, in the order the plan reaches them — which is the order the user watches it happen,
 *  and the order the closing message lists them in. */
export function bySheet(cards: ToolCard[]): SheetRun[] {
  const runs = new Map<string, SheetRun>();
  for (const card of cards) {
    const sheet = card.target?.sheet ?? (typeof card.input?.sheet === 'string' ? card.input.sheet : '') ?? '';
    const key = sheet || '—';
    const run = runs.get(key) ?? { sheet: key, cards: [], done: 0, failed: 0, running: false };
    run.cards.push(card);
    if (SETTLED.includes(card.status)) run.done += 1;
    if (card.status === 'error') run.failed += 1;
    if (card.status === 'running') run.running = true;
    runs.set(key, run);
  }
  return [...runs.values()];
}

/** A batch of client tool calls as one card: what is being done, how far it has got, and which sheet it is
 *  in. Each sheet opens onto its own calls.
 *
 *  It outlives the run, unlike a plain step — collapsed to a single line once everything has settled, so the
 *  turn keeps a receipt of what it wrote and a way back into the detail. A call that failed shows, in amber,
 *  only while the batch runs: the receipt lists what was written and nothing else, like a step that failed
 *  and left. The agent reads the failure and says in its answer what it did about it. */
export function BatchProgress({ cards: all, onShow }: { cards: ToolCard[]; onShow?: (c: ToolCard) => void }) {
  const { t } = useTranslation();
  const finished = all.every((c) => SETTLED.includes(c.status));
  const cards = finished ? all.filter((c) => c.status !== 'error') : all;
  const runs = bySheet(cards);
  const done = cards.filter((c) => SETTLED.includes(c.status)).length;
  const failed = cards.filter((c) => c.status === 'error').length;
  const [open, setOpen] = useState(false);
  const writing = cards.some((c) => c.risk === 'write' || c.risk === 'destructive');
  const percent = cards.length ? Math.round((done / cards.length) * 100) : 0;
  if (!cards.length) return null;

  if (finished && !open) {
    const label = writing
      ? t('batch.doneWriting', { count: cards.length, sheets: runs.length })
      : t('batch.doneReading', { count: cards.length, sheets: runs.length });
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="batch-done"
        className="flex min-h-[26px] w-full animate-pc-in-enter items-center gap-2 rounded-md bg-code px-2 py-1 text-left text-[12px] text-muted hover:text-ink"
      >
        <StepDot status="done" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <Chevron open={false} />
      </button>
    );
  }

  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="batch-progress">
      <div className="flex flex-col gap-1.5 px-3 py-2">
        <div className="flex items-center gap-2 text-[12px]">
          <StepDot status={failed ? 'error' : finished ? 'done' : 'running'} />
          <span className="min-w-0 flex-1 truncate font-medium text-ink">
            {writing
              ? t('batch.writing', { count: cards.length, sheets: runs.length })
              : t('batch.reading', { count: cards.length, sheets: runs.length })}
          </span>
          {finished && (
            <button type="button" onClick={() => setOpen(false)} className="shrink-0 text-muted hover:text-ink" aria-label={t('batch.collapse')}>
              <Chevron open />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-sm bg-line-soft" aria-hidden="true">
            <div className="h-1 bg-accent transition-all duration-300" style={{ width: `${percent}%` }} />
          </div>
          <span className="shrink-0 font-mono text-[11px] text-fainter" role="status">
            {t('batch.progress', { done, total: cards.length })}
          </span>
        </div>
      </div>
      <div className="border-t border-line-row">
        {runs.map((run) => (
          <SheetRow key={run.sheet} run={run} onShow={onShow} />
        ))}
      </div>
    </div>
  );
}

/** One sheet of the batch: its state, its share of the calls, and its calls when opened. */
function SheetRow({ run, onShow }: { run: SheetRun; onShow?: (c: ToolCard) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const finished = run.done === run.cards.length;
  const status: ToolCard['status'] = run.failed ? 'error' : run.running ? 'running' : finished ? 'done' : 'pending';
  const count = finished || !run.done ? t('batch.calls', { count: run.cards.length }) : t('batch.progress', { done: run.done, total: run.cards.length });
  return (
    <div className="border-b border-line-row last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-[26px] w-full items-center gap-2 px-3 text-left text-[12px] hover:bg-hover-soft"
        data-sheet-status={status}
      >
        <StepDot status={status} />
        <span className={`min-w-0 flex-1 truncate ${run.failed ? 'text-warn' : finished ? 'text-muted' : 'text-ink'}`}>{run.sheet}</span>
        <span className="shrink-0 font-mono text-[11px] text-fainter">{count}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="flex flex-col border-t border-line-row bg-code/60 py-0.5 pl-8 pr-3">
          {run.cards.map((card) => (
            <CallRow key={card.tool_call_id} card={card} onShow={onShow} />
          ))}
        </div>
      )}
    </div>
  );
}

function CallRow({ card, onShow }: { card: ToolCard; onShow?: (c: ToolCard) => void }) {
  const target = card.target ? qualify(card.target.sheet, card.target.address) : null;
  const failed = card.status === 'error';
  const clickable = !!card.target && !!onShow && (failed || card.status === 'done');
  return (
    <div className="flex h-[22px] items-center gap-2 text-[11.5px]" data-step-status={card.status} title={card.error ?? undefined}>
      <StepDot status={card.status} />
      <span className={`min-w-0 flex-1 truncate ${failed ? 'text-warn' : 'text-muted'}`}>
        {toolLabel(card.tool_name)}
        {failed && card.error ? <span className="text-warn"> — {card.error}</span> : null}
      </span>
      {target && (
        <button
          type="button"
          disabled={!clickable}
          onClick={() => onShow?.(card)}
          className={`shrink-0 font-mono text-[11px] text-faint ${clickable ? 'hover:text-accent' : 'cursor-default'}`}
        >
          {target}
        </button>
      )}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span aria-hidden="true" className={`shrink-0 text-[9px] text-fainter transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>
      ▶
    </span>
  );
}
